/**
 * @fileoverview Author schemas for author validation and type definitions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

// Helper function for optional URL fields that can be empty strings or null
const optionalUrlField = () => z.union([z.string().url(), z.literal(''), z.null()]).optional();

// Base Author schema without defaults (for partial updates)
const BaseAuthorSchema = z.object({
  id: z.string().uuid().openapi({
    description: 'Unique identifier for the author',
    example: '550e8400-e29b-41d4-a716-446655440000',
  }),
  name: z.string().openapi({
    description: 'Full name of the author',
    example: 'John Doe',
  }),
  alias: z.string().optional().nullable().openapi({
    description: 'Author alias or pen name',
    example: 'johndoe',
  }),
  avatar: optionalUrlField().nullable().openapi({
    description: 'URL to author avatar image',
    example: 'https://example.com/avatar.jpg',
  }),
  email: z.string().email().optional().nullable().openapi({
    description: 'Email address of the author',
    example: 'john@example.com',
  }),
  website: optionalUrlField().nullable().openapi({
    description: 'Author personal website URL',
    example: 'https://johndoe.com',
  }),
  xHandle: z.string().optional().nullable().openapi({
    description: 'X (Twitter) handle without @',
    example: 'johndoe',
  }),
  linkedinProfile: optionalUrlField().nullable().openapi({
    description: 'LinkedIn profile URL',
    example: 'https://linkedin.com/in/johndoe',
  }),
  blueskyHandle: z.string().optional().nullable().openapi({
    description: 'Bluesky handle without @',
    example: 'johndoe.bsky.social',
  }),
  github: z.string().optional().nullable().openapi({
    description: 'GitHub username',
    example: 'johndoe',
  }),
  company: z.string().optional().nullable().openapi({
    description: 'Author current company or organization',
    example: 'Acme Corp',
  }),
  aiContext: z
    .object({
      relationship: z.enum(['friend', 'colleague', 'acquaintance', 'admired', 'neutral']).optional().openapi({
        description: 'Relationship type with this author',
        example: 'friend',
      }),
      anecdotes: z.array(z.string()).optional().openapi({
        description: 'Personal anecdotes or history with this author',
        example: ['Met at re:Invent 2019', 'Collaborated on Lambda article'],
      }),
      toneGuidance: z.string().optional().openapi({
        description: 'How to adjust tone when referencing this author',
        example: 'Can be more casual, reference collaborations',
      }),
      expertise: z.array(z.string()).optional().openapi({
        description: 'Areas of expertise for this author',
        example: ['Lambda', 'observability', 'cost optimization'],
      }),
      mentionStyle: z.string().optional().openapi({
        description: 'Preferred way to mention this author',
        example: 'Usually refer to as "Yan" after first mention',
      }),
    })
    .optional()
    .nullable()
    .openapi({
      description: 'AI generation context for personalized blurb generation',
    }),
  createdAt: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the author was created',
    example: '2024-01-15T10:30:00Z',
  }),
  updatedAt: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the author was last updated',
    example: '2024-01-15T14:45:00Z',
  }),
});

// Full Author schema (no additional defaults needed for this model)
export const AuthorSchema = BaseAuthorSchema.openapi('Author');

// Create schema - omit auto-generated fields
export const CreateAuthorSchema = AuthorSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
  .extend({
    // Make name required with min length for creation
    name: z.string().min(1).openapi({
      description: 'Full name of the author (required)',
      example: 'Jane Smith',
    }),
  })
  .openapi('CreateAuthor');

// Update schema - partial from BASE to avoid applying defaults
// Uses union types to allow null for clearing fields
export const UpdateAuthorSchema = z
  .object({
    name: z.union([z.string().min(1), z.null()]).optional().openapi({
      description: 'Updated full name (null to clear)',
      example: 'Jane Doe',
    }),
    alias: z.union([z.string(), z.null()]).optional().openapi({
      description: 'Updated author alias or pen name (null to clear)',
      example: 'janedoe',
    }),
    avatar: z.union([z.string().url(), z.literal(''), z.null()]).optional().openapi({
      description: 'Updated avatar image URL (null to clear)',
      example: 'https://example.com/new-avatar.jpg',
    }),
    email: z.union([z.string().email(), z.literal(''), z.null()]).optional().openapi({
      description: 'Updated email address (null to clear)',
      example: 'jane.doe@example.com',
    }),
    website: z.union([z.string().url(), z.literal(''), z.null()]).optional().openapi({
      description: 'Updated website URL (null to clear)',
      example: 'https://janedoe.com',
    }),
    xHandle: z.union([z.string(), z.null()]).optional().openapi({
      description: 'Updated X (Twitter) handle without @ (null to clear)',
      example: 'janedoe',
    }),
    linkedinProfile: z.union([z.string().url(), z.literal(''), z.null()]).optional().openapi({
      description: 'Updated LinkedIn profile URL (null to clear)',
      example: 'https://linkedin.com/in/janedoe',
    }),
    blueskyHandle: z.union([z.string(), z.null()]).optional().openapi({
      description: 'Updated Bluesky handle without @ (null to clear)',
      example: 'janedoe.bsky.social',
    }),
    github: z.union([z.string(), z.null()]).optional().openapi({
      description: 'Updated GitHub username (null to clear)',
      example: 'janedoe',
    }),
    company: z.union([z.string(), z.null()]).optional().openapi({
      description: 'Updated company or organization (null to clear)',
      example: 'New Tech Corp',
    }),
    aiContext: z
      .union([
        z.object({
          relationship: z.enum(['friend', 'colleague', 'acquaintance', 'admired', 'neutral']).optional(),
          anecdotes: z.array(z.string()).optional(),
          toneGuidance: z.string().optional(),
          expertise: z.array(z.string()).optional(),
          mentionStyle: z.string().optional(),
        }),
        z.null(),
      ])
      .optional()
      .openapi({
        description: 'Updated AI generation context (null to clear)',
      }),
  })
  .openapi('UpdateAuthor');

export const AuthorResponseSchema = AuthorSchema.openapi('AuthorResponse');

export const AuthorListResponseSchema = createListResponseSchema(
  AuthorResponseSchema,
  'authors',
  'AuthorListResponse'
);

/**
 * JSDoc type definition for Author - Author profile type
 * @typedef {Object} AuthorType
 * @property {string} id - Unique identifier for the author
 * @property {string} name - Full name of the author
 * @property {string|null} [alias] - Author alias or pen name
 * @property {string|null} [avatar] - URL to author avatar image
 * @property {string|null} [email] - Email address of the author
 * @property {string|null} [website] - Author personal website URL
 * @property {string|null} [xHandle] - X (Twitter) handle without @
 * @property {string|null} [linkedinProfile] - LinkedIn profile URL
 * @property {string|null} [blueskyHandle] - Bluesky handle without @
 * @property {string|null} [github] - GitHub username
 * @property {string|null} [company] - Author current company or organization
 * @property {Object|null} [aiContext] - AI generation context for personalized blurb generation
 * @property {'friend'|'colleague'|'acquaintance'|'admired'|'neutral'} [aiContext.relationship] - Relationship type
 * @property {string[]} [aiContext.anecdotes] - Personal anecdotes or history
 * @property {string} [aiContext.toneGuidance] - How to adjust tone
 * @property {string[]} [aiContext.expertise] - Areas of expertise
 * @property {string} [aiContext.mentionStyle] - Preferred way to mention
 * @property {string} createdAt - ISO 8601 timestamp when the author was created
 * @property {string} updatedAt - ISO 8601 timestamp when the author was last updated
 */

export const AuthorType = /** @type {AuthorType} */ ({});
